import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/primitives';
import { ApiError } from '@/lib/api';
import { updateLead, newUpdateKey } from '@/data/leads';
import {
  EDIT_SECTIONS, LEAD_OPTIONS, diffLeadEdit, leadToEditDraft, optionHelp, validateLeadEdit,
  categoryDraftFor, diffCategories, pickableCategories, categoryName, leadCategoriesForLead, resolveCategory,
  type LeadCategoryList, type EditFieldDef, type EditableField, type EditableTextField, type LeadEditPatch, type LeadLinks, type LeadRecord, type LeadChange,
} from '@/data/leads-model';
import { cn } from '@/lib/utils';
import { useLeadCategories } from '@/hooks/useLeadCategories';

/* Full owner edit of a lead — every owner-editable field, grouped in sections
 * (Business / Booking info / Sales). Works from a phone (full-width sheet) and
 * from a desktop (wider slide-over on the lead page). ONE PATCH per Save with
 * ONE idempotency key per decision (a retry replays, never double-applies); only
 * changed fields are sent (diffLeadEdit). Option lists + labels come from the
 * central config (leads-config.ts) — nothing is hard-coded here. The server is
 * the authority (whitelist, validation, 409 duplicate email) and audits every
 * change on the lead's History. Internal fields never appear in this form. */

const inputCls = 'h-11 w-full rounded-md border border-border-default bg-surface px-3 text-sm text-text-primary disabled:opacity-60';
const areaCls = 'min-h-[88px] w-full rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-text-primary';

export function LeadEditSheet({ lead, open, onClose, onSaved, projectId }: {
  lead: LeadRecord | null; open: boolean; onClose: () => void;
  onSaved: (lead: LeadRecord, changes: LeadChange[]) => void;
  projectId?: number | null;
}) {
  const [draft, setDraft] = useState<LeadEditPatch>({});
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; duplicateRef?: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<EditableField, string>>>({});
  const [listingsText, setListingsText] = useState('');
  const [cats, setCats] = useState<string[] | null>(null);
  const { list: categoryList } = useLeadCategories();

  useEffect(() => {
    if (lead && open) {
      setDraft(leadToEditDraft(lead));
      setListingsText((lead.links?.listings ?? []).join('\n'));
      setCats(categoryDraftFor(lead));
      setKey(newUpdateKey());
      setError(null);
      setFieldErrors({});
    }
  }, [lead, open]);

  const fieldChanges = useMemo(() => (lead ? diffLeadEdit(lead, draft) : {}), [lead, draft]);
  const catChange = useMemo(() => (lead ? diffCategories(lead, cats) : undefined), [lead, cats]);
  const changes = useMemo(() => (catChange !== undefined ? { ...fieldChanges, categories: catChange } : fieldChanges), [fieldChanges, catChange]);
  const dirty = Object.keys(changes).length > 0;
  if (!lead) return null;

  const setText = (name: EditableTextField, v: string) => setDraft((d) => ({ ...d, [name]: v === '' ? null : v }));
  const setBool = (name: 'subscribed' | 'content_shoot_booked', v: string) => setDraft((d) => ({ ...d, [name]: v === '' ? null : v === 'yes' }));
  const setLink = (k: 'maps' | 'search' | 'source', v: string) => setDraft((d) => {
    const links: LeadLinks = { ...(d.links ?? {}) };
    if (v.trim()) links[k] = v.trim(); else delete links[k];
    return { ...d, links };
  });
  const setListings = (text: string) => {
    setListingsText(text);
    setDraft((d) => {
      const links: LeadLinks = { ...(d.links ?? {}) };
      const list = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      if (list.length) links.listings = list; else delete links.listings;
      return { ...d, links };
    });
  };

  const save = async () => {
    if (!dirty) { onClose(); return; }
    const errs = validateLeadEdit(fieldChanges);
    setFieldErrors(errs);
    if (Object.keys(errs).length) { setError({ message: 'Please fix the highlighted fields.' }); return; }
    setBusy(true); setError(null);
    try {
      const res = await updateLead(lead.lead_ref, changes, key, projectId ?? undefined);
      onSaved(res.lead, res.changes ?? []);
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const dup = (e.payload as { duplicate?: { lead_ref?: string } } | undefined)?.duplicate?.lead_ref;
        setError({ message: e.message, duplicateRef: dup });
        if (e.code === 'duplicate_email' || e.code === 'email_suppressed' || e.code === 'contact_locked') setFieldErrors({ business_email: e.message });
      } else {
        setError({ message: 'Could not save — check your connection and tap Save again (safe to retry).' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={<span className="truncate">Edit — {lead.business_name ?? lead.lead_ref}</span>} widthClass="w-full lg:max-w-[640px]">
      <div className="space-y-5 pb-24" data-testid="lead-edit">
        {EDIT_SECTIONS.map((section) => (
          <details key={section.key} open className="group rounded-lg border border-border-subtle" data-testid={`edit-section-${section.key}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5">
              <span>
                <span className="block text-sm font-semibold text-text-primary">{section.title}</span>
                {section.blurb ? <span className="block text-[11px] text-text-tertiary">{section.blurb}</span> : null}
              </span>
              <span className="text-xs text-text-tertiary group-open:rotate-180" aria-hidden>⌄</span>
            </summary>
            <div className="grid gap-3 px-3 pb-3 sm:grid-cols-2">
              {section.fields.map((f) => (
                <FieldControl
                  key={f.name} def={f} draft={draft} error={fieldErrors[f.name]} listingsText={listingsText}
                  onText={setText} onBool={setBool} onLink={setLink} onListings={setListings}
                />
              ))}
              {section.key === 'business' ? (
                <CategoryPicker list={categoryList} value={cats} onChange={setCats} autoSlugs={[...new Set(leadCategoriesForLead(draft.modality ?? lead.modality, draft.business_name ?? lead.business_name).map((c) => resolveCategory(c, categoryList)))]} />
              ) : null}
            </div>
          </details>
        ))}

        {error ? (
          <p className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
            {error.message}
            {error.duplicateRef ? <> <Link className="underline" to={`/sales/leads/${encodeURIComponent(error.duplicateRef)}`} onClick={onClose}>Open {error.duplicateRef}</Link></> : null}
          </p>
        ) : null}

        <div className="sticky bottom-0 flex gap-2 border-t border-border-subtle bg-surface py-3">
          <Button variant="primary" className="h-11 flex-1" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : dirty ? `Save ${Object.keys(changes).length} change${Object.keys(changes).length > 1 ? 's' : ''}` : 'Nothing changed'}
          </Button>
          <Button variant="secondary" className="h-11" onClick={onClose} disabled={busy}>Cancel</Button>
        </div>
        <p className="text-[11px] text-text-tertiary">Every saved change is recorded on the lead's History (what changed, from → to). Re-uploading the workbook later never overwrites what you set here.</p>
      </div>
    </Drawer>
  );
}

function FieldControl({ def, draft, error, listingsText, onText, onBool, onLink, onListings }: {
  def: EditFieldDef; draft: LeadEditPatch; error?: string; listingsText: string;
  onText: (name: EditableTextField, v: string) => void;
  onBool: (name: 'subscribed' | 'content_shoot_booked', v: string) => void;
  onLink: (k: 'maps' | 'search' | 'source', v: string) => void;
  onListings: (text: string) => void;
}) {
  const wide = def.type === 'textarea' || def.type === 'links' || def.name === 'address' || def.name === 'booking_platform_kind';
  const id = `edit-${def.name}`;
  const label = (
    <span className="mb-1 block text-[11px] uppercase tracking-wide text-text-tertiary">
      {def.label}{def.required ? <span className="text-danger"> *</span> : null}
    </span>
  );
  const err = error ? <span className="mt-1 block text-[11px] text-danger">{error}</span> : null;
  const help = def.help ? <span className="mt-1 block text-[11px] text-text-tertiary">{def.help}</span> : null;

  if (def.type === 'links') {
    const l = draft.links ?? {};
    return (
      <div className={cn('sm:col-span-2')} data-testid="edit-links">
        {label}
        <div className="grid gap-2 sm:grid-cols-3">
          {(['maps', 'search', 'source'] as const).map((k) => (
            <input key={k} id={`${id}-${k}`} type="url" inputMode="url" className={inputCls} placeholder={k === 'maps' ? 'Google Maps link' : k === 'search' ? 'Google search link' : 'Business / source link'} value={l[k] ?? ''} onChange={(e) => onLink(k, e.target.value)} aria-label={`${def.label} — ${k}`} />
          ))}
        </div>
        <textarea id={`${id}-listings`} className={cn(areaCls, 'mt-2')} rows={3} placeholder="Public listing pages — one link per line" value={listingsText} onChange={(e) => onListings(e.target.value)} aria-label={`${def.label} — public listings`} />
        {help}{err}
      </div>
    );
  }
  if (def.type === 'bool') {
    const name = def.name as 'subscribed' | 'content_shoot_booked';
    const v = draft[name];
    return (
      <label className="block" htmlFor={id}>
        {label}
        <select id={id} className={inputCls} value={v === null || v === undefined ? '' : v ? 'yes' : 'no'} onChange={(e) => onBool(name, e.target.value)}>
          <option value="">Not set</option><option value="yes">Yes</option><option value="no">No</option>
        </select>
        {err}
      </label>
    );
  }
  const name = def.name as EditableTextField;
  const value = draft[name] ?? '';
  if (def.type === 'select' && def.options) {
    const options = LEAD_OPTIONS[def.options];
    const selectedHelp = optionHelp(options, value);
    return (
      <div className={cn(wide && 'sm:col-span-2')}>
        <label className="block" htmlFor={id}>
          {label}
          <select id={id} className={inputCls} value={value} onChange={(e) => onText(name, e.target.value)}>
            {!def.required ? <option value="">Not set</option> : null}
            {options.map((o) => <option key={o.value} value={o.value} title={o.help}>{o.label}</option>)}
          </select>
        </label>
        {selectedHelp ? <span className="mt-1 block text-[11px] text-text-secondary" data-testid={`${id}-help`}>{selectedHelp}</span> : null}
        {def.options === 'platformKinds' ? (
          <details className="mt-1 text-[11px] text-text-tertiary">
            <summary className="cursor-pointer">What do these mean?</summary>
            <ul className="mt-1 space-y-1">
              {options.map((o) => <li key={o.value}><span className="font-medium text-text-secondary">{o.label}:</span> {o.help}</li>)}
            </ul>
          </details>
        ) : null}
        {err}
      </div>
    );
  }
  if (def.type === 'textarea') {
    return (
      <label className="block sm:col-span-2" htmlFor={id}>
        {label}
        <textarea id={id} className={areaCls} rows={def.name === 'platform_evidence_note' ? 2 : 4} maxLength={def.maxLength} value={value} onChange={(e) => onText(name, e.target.value)} placeholder={def.placeholder} />
        {help}{err}
      </label>
    );
  }
  const inputType = def.type === 'email' ? 'email' : def.type === 'tel' ? 'tel' : def.type === 'date' ? 'date' : def.type === 'url' ? 'text' : 'text';
  const inputMode = def.type === 'email' ? 'email' : def.type === 'tel' ? 'tel' : def.type === 'url' ? 'url' : undefined;
  return (
    <label className={cn('block', wide && 'sm:col-span-2')} htmlFor={id}>
      {label}
      <input id={id} type={inputType} inputMode={inputMode} className={cn(inputCls, error && 'border-danger')} maxLength={def.maxLength} value={value} placeholder={def.placeholder} onChange={(e) => onText(name, e.target.value)} autoCapitalize={def.type === 'email' || def.type === 'url' ? 'none' : undefined} />
      {help}{err}
    </label>
  );
}

/* item 7: categories — "Automatic" (derived live from the business type above) or a
 * hand-picked set from the workspace list. Disabled (read-only preview) until the
 * categories column exists on the server (column_enabled). */
function CategoryPicker({ list, value, onChange, autoSlugs }: {
  list: LeadCategoryList | null; value: string[] | null; onChange: (v: string[] | null) => void; autoSlugs: string[];
}) {
  const enabled = list?.column_enabled === true;
  const manual = value !== null;
  const options = pickableCategories(list, value ?? []);
  const toggle = (slug: string) => {
    const cur = value ?? autoSlugs.filter((s) => options.some((o) => o.slug === s));
    onChange(cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  };
  return (
    <div className="sm:col-span-2" data-testid="edit-categories">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-text-tertiary">Categories</span>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <button type="button" className={cn('rounded-full border px-2.5 py-1', !manual ? 'border-engine bg-engine/10 text-text-primary' : 'border-border-default text-text-secondary')} aria-pressed={!manual} onClick={() => onChange(null)} disabled={!enabled && manual === false}>
          Automatic
        </button>
        <span className="text-text-tertiary">{!manual ? `From the business type and name: ${autoSlugs.map((s) => categoryName(s, list)).join(', ')}` : 'Chosen by hand'}</span>
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Choose categories">
        {options.map((o) => {
          const on = manual ? value!.includes(o.slug) : autoSlugs.includes(o.slug);
          return (
            <button key={o.slug} type="button" disabled={!enabled} aria-pressed={on} onClick={() => toggle(o.slug)}
              className={cn('rounded-full border px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60', on ? 'border-engine bg-engine/10 text-text-primary' : 'border-border-default text-text-secondary hover:bg-hover')}>
              {o.name}
            </button>
          );
        })}
      </div>
      {!enabled ? (
        <span className="mt-1 block text-[11px] text-text-tertiary" data-testid="categories-locked">Worked out automatically from the business type for now. Choosing categories by hand switches on after a one-off database update that is awaiting approval.</span>
      ) : manual && value!.length === 0 ? (
        <span className="mt-1 block text-[11px] text-text-tertiary">No category picked — saving goes back to Automatic.</span>
      ) : null}
    </div>
  );
}
